import { useEffect, useRef, useState, useCallback } from 'react'
import mapboxgl from 'mapbox-gl'
import { CrimeFilters, LocationData } from '../lib/types'

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN || ''

interface MapProps {
  filters: CrimeFilters
  searchQuery: string
  selectedMunicipalityId?: string | null
  onLocationSelect: (location: LocationData | null) => void
}

const MEXICO_BOUNDS: [[number, number], [number, number]] = [
  [-118.5, 14.5],
  [-86.5, 32.7],
]

const MEXICO_CENTER: [number, number] = [-102.5, 23.6]

const NO_DATA_COLOR = '#141414'

// Sequential (single-hue-ish) ramp for a more "uniform" choropleth on a dark basemap.
// Low = light sand, high = deep red.
const DYNAMIC_COLOR_RAMP = [
  '#f2e9dc',
  '#e6cfb3',
  '#d7b08c',
  '#c9895f',
  '#b56744',
  '#9a3f33',
  '#7b1e1e',
] as const

// Used only as a stable fallback when the filtered distribution has no variance.
const ABSOLUTE_COLOR_STOPS = [
  { threshold: 0, color: DYNAMIC_COLOR_RAMP[0] },
  { threshold: 200, color: DYNAMIC_COLOR_RAMP[1] },
  { threshold: 350, color: DYNAMIC_COLOR_RAMP[2] },
  { threshold: 500, color: DYNAMIC_COLOR_RAMP[3] },
  { threshold: 650, color: DYNAMIC_COLOR_RAMP[4] },
  { threshold: 800, color: DYNAMIC_COLOR_RAMP[5] },
  { threshold: 950, color: DYNAMIC_COLOR_RAMP[6] },
] as const

// Rough absolute stops for municipal *counts* (used only when the distribution has no variance).
const ABSOLUTE_COUNT_STOPS = [
  { threshold: 0, color: DYNAMIC_COLOR_RAMP[0] },
  { threshold: 100, color: DYNAMIC_COLOR_RAMP[1] },
  { threshold: 300, color: DYNAMIC_COLOR_RAMP[2] },
  { threshold: 800, color: DYNAMIC_COLOR_RAMP[3] },
  { threshold: 2000, color: DYNAMIC_COLOR_RAMP[4] },
  { threshold: 6000, color: DYNAMIC_COLOR_RAMP[5] },
  { threshold: 15000, color: DYNAMIC_COLOR_RAMP[6] },
] as const

type ColorScale = {
  colors: string[]
  breaks: number[]
  min: number
  max: number
}

function roundTo1Decimal(value: number): number {
  return Math.round(value * 10) / 10
}

function buildLogInterpolateExpression(
  minRate: number,
  maxRate: number,
  colors: readonly string[],
  valueExpression: any[] = ['get', 'crimeRate']
) {
  const minLog = Math.log10(minRate + 1)
  const maxLog = Math.log10(maxRate + 1)

  const expression: any[] = [
    'interpolate',
    ['linear'],
    ['log10', ['+', valueExpression, 1]],
  ]

  if (minLog === maxLog || colors.length === 0) return expression

  // Mapbox requires stop inputs to be strictly increasing; keep full precision and
  // de-dupe with a tiny epsilon if values are extremely close.
  const steps = Math.max(colors.length - 1, 1)
  let prevStop = Number.NEGATIVE_INFINITY
  for (let i = 0; i < colors.length; i++) {
    const t = i / steps
    let stop = minLog + (maxLog - minLog) * t
    if (stop <= prevStop) stop = prevStop + 1e-6
    prevStop = stop
    expression.push(stop, colors[i])
  }

  return expression
}

function pickAbsoluteColor(rate: number): string {
  let color: string = ABSOLUTE_COLOR_STOPS[0].color
  for (const stop of ABSOLUTE_COLOR_STOPS) {
    if (rate >= stop.threshold) color = stop.color
  }
  return color
}

function pickAbsoluteCountColor(count: number): string {
  let color: string = ABSOLUTE_COUNT_STOPS[0].color
  for (const stop of ABSOLUTE_COUNT_STOPS) {
    if (count >= stop.threshold) color = stop.color
  }
  return color
}

// Raw data storage
interface RawStateData {
  id: string
  name: string
  population: number
  crimes: Record<string, number>
  total: number
  rate: number
}

interface RawCrimeData {
  states: RawStateData[]
}

export default function Map({
  filters,
  searchQuery,
  selectedMunicipalityId,
  onLocationSelect,
}: MapProps) {
  const mapContainer = useRef<HTMLDivElement>(null)
  const map = useRef<mapboxgl.Map | null>(null)
  const focusedMunicipality = useRef<string | null>(null)
  const [mapLoaded, setMapLoaded] = useState(false)
  const [tokenMissing, setTokenMissing] = useState(false)
  const [geoData, setGeoData] = useState<any>(null)
  const [crimeData, setCrimeData] = useState<RawCrimeData | null>(null)
  const [municipalGeoData, setMunicipalGeoData] = useState<any>(null)
  const [stateColorScale, setStateColorScale] = useState<ColorScale | null>(null)
  const [municipalColorScale, setMunicipalColorScale] = useState<ColorScale | null>(null)
  const [mapZoom, setMapZoom] = useState<number>(5)

  // Calculate filtered stats based on selected crime types
  const calculateFilteredStats = useCallback((state: RawStateData, crimeTypes: CrimeFilters['crimeTypes']) => {
    const enabledTypes = Object.entries(crimeTypes)
      .filter(([_, enabled]) => enabled)
      .map(([type]) => type)

    if (enabledTypes.length === 0) {
      return { total: 0, rate: null as number | null }
    }

    const filteredTotal = enabledTypes.reduce((sum, type) => {
      return sum + (state.crimes[type] || 0)
    }, 0)

    const rate = (filteredTotal / state.population) * 100000

    return { total: filteredTotal, rate: roundTo1Decimal(rate) }
  }, [])

  const calculateFilteredStatsFromFeature = useCallback(
    (feature: any, crimeTypes: CrimeFilters['crimeTypes']) => {
      const enabledTypes = Object.entries(crimeTypes)
        .filter(([_, enabled]) => enabled)
        .map(([type]) => type)

      if (enabledTypes.length === 0) {
        return { total: 0, rate: null as number | null }
      }

      const props = feature?.properties || {}
      const population = Number(props.population) || 0
      const filteredTotal = enabledTypes.reduce((sum, type) => sum + (Number(props[type]) || 0), 0)
      if (population <= 0) return { total: filteredTotal, rate: null as number | null }

      const rate = (filteredTotal / population) * 100000
      return { total: filteredTotal, rate: roundTo1Decimal(rate) }
    },
    []
  )

  // Deep-link support: if the URL specifies a municipality id, focus it once data is ready.
  useEffect(() => {
    if (!map.current || !mapLoaded) return
    if (!filters || !municipalGeoData) return

    // Reset the focus guard if the param was cleared.
    if (!selectedMunicipalityId) {
      focusedMunicipality.current = null
      return
    }

    if (focusedMunicipality.current === selectedMunicipalityId) return

    const feature = municipalGeoData.features?.find(
      (f: any) => String(f?.properties?.id || '') === selectedMunicipalityId
    )
    if (!feature) return

    const coords = feature.geometry?.coordinates
    if (Array.isArray(coords) && coords.length === 2) {
      map.current.flyTo({ center: coords as [number, number], zoom: 10, duration: 800 })
    }

    const props = feature.properties || {}
    const { total, rate } = calculateFilteredStatsFromFeature(feature, filters.crimeTypes)
    const safeRate = typeof rate === 'number' ? rate : 0

    const locationData: LocationData = {
      id: props.id || '',
      name: props.name || '',
      state: props.state || '',
      type: 'municipality',
      coordinates: (Array.isArray(coords) ? coords : map.current.getCenter().toArray()) as [
        number,
        number,
      ],
      crimeStats: {
        total,
        homicidio_doloso: props.homicidio_doloso || 0,
        feminicidio: props.feminicidio || 0,
        secuestro: props.secuestro || 0,
        extorsion: props.extorsion || 0,
        robo_vehiculo: props.robo_vehiculo || 0,
        robo_casa: props.robo_casa || 0,
        robo_negocio: props.robo_negocio || 0,
        robo_transeunte: props.robo_transeunte || 0,
        lesiones_dolosas: props.lesiones_dolosas || 0,
        violencia_familiar: props.violencia_familiar || 0,
        violacion: props.violacion || 0,
        narcomenudeo: props.narcomenudeo || 0,
        rate_per_100k: safeRate,
        trend: 'stable',
        trend_percentage: 0,
      },
      population: props.population || 0,
      riskLevel: getRiskLevel(safeRate),
    }

    onLocationSelect(locationData)
    focusedMunicipality.current = selectedMunicipalityId
  }, [
    mapLoaded,
    municipalGeoData,
    filters,
    selectedMunicipalityId,
    calculateFilteredStatsFromFeature,
    onLocationSelect,
  ])

  // Update map with filtered data
  const updateMapData = useCallback(() => {
    if (!map.current || !geoData || !crimeData || !mapLoaded) return

    const source = map.current.getSource('mexico-states') as mapboxgl.GeoJSONSource
    if (!source) return

    // Create a map of state name to crime data
    const crimeByState: Record<string, RawStateData> = {}
    for (const state of crimeData.states) {
      crimeByState[state.name] = state
    }

    // Merge and filter crime data into GeoJSON
    const mergedFeatures = geoData.features.map((feature: any) => {
      const stateName = feature.properties?.name || feature.properties?.ESTADO || ''
      const rawState = crimeByState[stateName]

      if (!rawState) {
        return {
          ...feature,
          properties: {
            ...feature.properties,
            name: stateName,
            totalCrimes: 0,
            crimeRate: null,
            population: 0,
          },
        }
      }

      // Calculate filtered totals
      const { total, rate } = calculateFilteredStats(rawState, filters.crimeTypes)

      return {
        ...feature,
        properties: {
          ...feature.properties,
          id: rawState.id,
          name: stateName,
          population: rawState.population,
          totalCrimes: total,
          crimeRate: rate,
          // Include individual crime counts for click handler
          ...rawState.crimes,
        },
      }
    })

    source.setData({
      type: 'FeatureCollection',
      features: mergedFeatures,
    })

    // Update fill colors based on the filtered distribution (quantile scale).
    const rates = mergedFeatures
      .map((f: any) => f.properties?.crimeRate)
      .filter((v: unknown): v is number => typeof v === 'number' && Number.isFinite(v))

    if (map.current.getLayer('state-fills')) {
      if (rates.length === 0) {
        map.current.setPaintProperty('state-fills', 'fill-color', [
          'case',
          ['==', ['get', 'crimeRate'], null],
          NO_DATA_COLOR,
          NO_DATA_COLOR,
        ])
        setStateColorScale(null)
      } else {
        const min = Math.min(...rates)
        const max = Math.max(...rates)

        if (min === max) {
          const constantColor = pickAbsoluteColor(min)
          map.current.setPaintProperty('state-fills', 'fill-color', [
            'case',
            ['==', ['get', 'crimeRate'], null],
            NO_DATA_COLOR,
            constantColor,
          ])
          setStateColorScale({
            colors: [constantColor],
            breaks: [],
            min,
            max,
          })
        } else {
          const interpolateExpression = buildLogInterpolateExpression(min, max, DYNAMIC_COLOR_RAMP)
          map.current.setPaintProperty('state-fills', 'fill-color', [
            'case',
            ['==', ['get', 'crimeRate'], null],
            NO_DATA_COLOR,
            interpolateExpression,
          ])
          setStateColorScale({
            colors: [...DYNAMIC_COLOR_RAMP],
            breaks: [],
            min,
            max,
          })
        }
      }
    }

    // Municipal points + clusters
    if (municipalGeoData && map.current.getSource('municipalities')) {
      const municipalFeatures = municipalGeoData.features.map((feature: any) => {
        const { total, rate } = calculateFilteredStatsFromFeature(feature, filters.crimeTypes)
        return {
          ...feature,
          properties: {
            ...feature.properties,
            totalCrimes: total,
            crimeRate: rate,
          },
        }
      })

      const muniSource = map.current.getSource('municipalities') as mapboxgl.GeoJSONSource
      muniSource.setData({ type: 'FeatureCollection', features: municipalFeatures })

      const muniTotals = municipalFeatures
        .filter((f: any) => (f.properties?.totalCrimes || 0) > 0)
        .map((f: any) => f.properties?.totalCrimes)
        .filter((v: unknown): v is number => typeof v === 'number' && Number.isFinite(v))

      const unclusteredLayer = map.current.getLayer('municipality-points')
      const clusterLayer = map.current.getLayer('municipality-clusters')

      if (unclusteredLayer && clusterLayer) {
        if (muniTotals.length === 0) {
          map.current.setPaintProperty('municipality-points', 'circle-color', NO_DATA_COLOR)
          map.current.setPaintProperty('municipality-clusters', 'circle-color', NO_DATA_COLOR)
          setMunicipalColorScale(null)
        } else {
          const min = Math.min(...muniTotals)
          const max = Math.max(...muniTotals)

          if (min === max) {
            const constantColor = pickAbsoluteCountColor(min)
            map.current.setPaintProperty('municipality-points', 'circle-color', constantColor)
            map.current.setPaintProperty('municipality-clusters', 'circle-color', constantColor)
            setMunicipalColorScale({ colors: [constantColor], breaks: [], min, max })
          } else {
            const unclusteredExpr = buildLogInterpolateExpression(min, max, DYNAMIC_COLOR_RAMP, [
              'get',
              'totalCrimes',
            ])
            const clusteredExpr = buildLogInterpolateExpression(min, max, DYNAMIC_COLOR_RAMP, [
              'get',
              'metric',
            ])

            map.current.setPaintProperty('municipality-points', 'circle-color', [
              'case',
              ['<=', ['get', 'totalCrimes'], 0],
              NO_DATA_COLOR,
              unclusteredExpr,
            ])
            map.current.setPaintProperty('municipality-clusters', 'circle-color', [
              'case',
              ['<=', ['get', 'metric'], 0],
              NO_DATA_COLOR,
              clusteredExpr,
            ])
            setMunicipalColorScale({ colors: [...DYNAMIC_COLOR_RAMP], breaks: [], min, max })
          }
        }
      }
    }

    // If a state filter is selected, zoom to it
    if (filters.state) {
      const selectedFeature = mergedFeatures.find(
        (f: any) => f.properties.name === filters.state
      )
      if (selectedFeature && selectedFeature.geometry) {
        // Get bounds of the feature
        const bounds = new mapboxgl.LngLatBounds()
        const addCoords = (coords: any) => {
          if (Array.isArray(coords[0])) {
            coords.forEach(addCoords)
          } else {
            bounds.extend(coords as [number, number])
          }
        }
        addCoords(selectedFeature.geometry.coordinates)
        map.current.fitBounds(bounds, { padding: 50, duration: 1000 })
      }
    }
  }, [
    geoData,
    crimeData,
    municipalGeoData,
    filters,
    mapLoaded,
    calculateFilteredStats,
    calculateFilteredStatsFromFeature,
  ])

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      try {
        const [geoResponse, crimeResponse, municipalResponse] = await Promise.all([
          fetch('/data/mexico-states.geojson'),
          fetch('/data/crime-data.json'),
          fetch('/data/municipal-points.geojson'),
        ])

        if (geoResponse.ok && crimeResponse.ok) {
          const geo = await geoResponse.json()
          const crime = await crimeResponse.json()
          setGeoData(geo)
          setCrimeData(crime)
        }
        if (municipalResponse.ok) {
          const municipal = await municipalResponse.json()
          setMunicipalGeoData(municipal)
        }
      } catch (error) {
        console.error('Failed to load data:', error)
      }
    }

    loadData()
  }, [])

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || map.current) return

    if (!MAPBOX_TOKEN) {
      setTokenMissing(true)
      return
    }

    mapboxgl.accessToken = MAPBOX_TOKEN
    // Avoid noisy console errors when clients block Mapbox telemetry endpoints.
    if (typeof (mapboxgl as any).setTelemetryEnabled === 'function') {
      ;(mapboxgl as any).setTelemetryEnabled(false)
    }

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/dark-v11',
      center: MEXICO_CENTER,
      zoom: 5,
      maxBounds: MEXICO_BOUNDS,
      minZoom: 4,
      maxZoom: 14,
    })

    map.current.addControl(
      new mapboxgl.NavigationControl({ visualizePitch: true }),
      'top-right'
    )

    map.current.on('load', () => {
      if (!map.current) return

      // Add empty source
      map.current.addSource('mexico-states', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      })

      map.current.addSource('municipalities', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
        cluster: true,
        clusterRadius: 50,
        clusterMaxZoom: 9,
        clusterProperties: {
          metric: ['+', ['get', 'totalCrimes']],
          pop: ['+', ['get', 'population']],
        },
      })

      // Dynamic color scale based on filtered data
      map.current.addLayer({
        id: 'state-fills',
        type: 'fill',
        source: 'mexico-states',
        paint: {
          // Updated dynamically in updateMapData() once data + filters are available
          'fill-color': NO_DATA_COLOR,
          'fill-opacity': 0.65,
        },
      })

      map.current.addLayer({
        id: 'state-borders',
        type: 'line',
        source: 'mexico-states',
        paint: {
          'line-color': 'rgba(255, 255, 255, 0.2)',
          'line-width': 0.8,
          'line-opacity': 0.9,
        },
      })

      map.current.addLayer({
        id: 'state-fills-hover',
        type: 'fill',
        source: 'mexico-states',
        paint: {
          'fill-color': '#ffffff',
          'fill-opacity': 0.15,
        },
        filter: ['==', 'name', ''],
      })

      // Municipal clusters (hotspots)
      map.current.addLayer({
        id: 'municipality-clusters',
        type: 'circle',
        source: 'municipalities',
        minzoom: 6,
        filter: ['all', ['has', 'point_count'], ['>', ['get', 'metric'], 0]],
        paint: {
          'circle-color': NO_DATA_COLOR,
          'circle-opacity': 0.78,
          'circle-radius': [
            'interpolate',
            ['linear'],
            ['sqrt', ['get', 'metric']],
            0,
            12,
            2000,
            18,
            10000,
            26,
            50000,
            38,
            200000,
            54,
          ],
          'circle-stroke-width': 1,
          'circle-stroke-color': 'rgba(255, 255, 255, 0.25)',
        },
      })

      map.current.addLayer({
        id: 'municipality-cluster-count',
        type: 'symbol',
        source: 'municipalities',
        minzoom: 6,
        filter: ['all', ['has', 'point_count'], ['>', ['get', 'metric'], 0]],
        layout: {
          'text-field': ['get', 'point_count_abbreviated'],
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 12,
        },
        paint: {
          'text-color': '#ffffff',
        },
      })

      map.current.addLayer({
        id: 'municipality-points',
        type: 'circle',
        source: 'municipalities',
        minzoom: 6,
        filter: ['all', ['!', ['has', 'point_count']], ['>', ['get', 'totalCrimes'], 0]],
        paint: {
          'circle-color': NO_DATA_COLOR,
          'circle-opacity': 0.9,
          'circle-radius': [
            'interpolate',
            ['linear'],
            ['sqrt', ['get', 'totalCrimes']],
            0,
            3,
            20,
            5,
            100,
            7,
            500,
            10,
            2000,
            16,
            10000,
            24,
          ],
          'circle-stroke-width': 0.8,
          'circle-stroke-color': 'rgba(0, 0, 0, 0.35)',
        },
      })

      // Click handler
      map.current.on('click', 'state-fills', (e) => {
        if (e.features && e.features[0]) {
          const props = e.features[0].properties
          if (!props) return

          const locationData: LocationData = {
            id: props.id || '',
            name: props.name || '',
            state: props.name || '',
            type: 'state',
            coordinates: e.lngLat.toArray() as [number, number],
            crimeStats: {
              total: props.totalCrimes || 0,
              homicidio_doloso: props.homicidio_doloso || 0,
              feminicidio: props.feminicidio || 0,
              secuestro: props.secuestro || 0,
              extorsion: props.extorsion || 0,
              robo_vehiculo: props.robo_vehiculo || 0,
              robo_casa: props.robo_casa || 0,
              robo_negocio: props.robo_negocio || 0,
              robo_transeunte: props.robo_transeunte || 0,
              lesiones_dolosas: props.lesiones_dolosas || 0,
              violencia_familiar: props.violencia_familiar || 0,
              violacion: props.violacion || 0,
              narcomenudeo: props.narcomenudeo || 0,
              rate_per_100k: props.crimeRate || 0,
              trend: 'stable',
              trend_percentage: 0,
            },
            population: props.population || 0,
            riskLevel: getRiskLevel(props.crimeRate || 0),
          }

          onLocationSelect(locationData)
        }
      })

      map.current.on('click', 'municipality-clusters', (e) => {
        if (!map.current || !e.features?.[0]) return
        const feature = e.features[0] as any
        const clusterId = feature.properties?.cluster_id
        const source = map.current.getSource('municipalities') as any
        if (clusterId == null || !source?.getClusterExpansionZoom) return

        source.getClusterExpansionZoom(clusterId, (err: any, zoom: number) => {
          if (err) return
          const coordinates = feature.geometry?.coordinates
          if (!Array.isArray(coordinates) || coordinates.length !== 2) return
          const center: [number, number] = [Number(coordinates[0]), Number(coordinates[1])]
          map.current?.easeTo({ center, zoom: Math.min(zoom, 12), duration: 500 })
        })
      })

      map.current.on('click', 'municipality-points', (e) => {
        if (e.features && e.features[0]) {
          const props = e.features[0].properties
          if (!props) return

          const rate = typeof props.crimeRate === 'number' ? props.crimeRate : 0
          const locationData: LocationData = {
            id: props.id || '',
            name: props.name || '',
            state: props.state || '',
            type: 'municipality',
            coordinates: e.lngLat.toArray() as [number, number],
            crimeStats: {
              total: props.totalCrimes || 0,
              homicidio_doloso: props.homicidio_doloso || 0,
              feminicidio: props.feminicidio || 0,
              secuestro: props.secuestro || 0,
              extorsion: props.extorsion || 0,
              robo_vehiculo: props.robo_vehiculo || 0,
              robo_casa: props.robo_casa || 0,
              robo_negocio: props.robo_negocio || 0,
              robo_transeunte: props.robo_transeunte || 0,
              lesiones_dolosas: props.lesiones_dolosas || 0,
              violencia_familiar: props.violencia_familiar || 0,
              violacion: props.violacion || 0,
              narcomenudeo: props.narcomenudeo || 0,
              rate_per_100k: rate,
              trend: 'stable',
              trend_percentage: 0,
            },
            population: props.population || 0,
            riskLevel: getRiskLevel(rate),
          }

          onLocationSelect(locationData)
        }
      })

      map.current.on('mouseenter', 'state-fills', () => {
        if (map.current) map.current.getCanvas().style.cursor = 'pointer'
      })

      map.current.on('mouseleave', 'state-fills', () => {
        if (map.current) {
          map.current.getCanvas().style.cursor = ''
          map.current.setFilter('state-fills-hover', ['==', 'name', ''])
        }
      })

      map.current.on('mouseenter', 'municipality-clusters', () => {
        if (map.current) map.current.getCanvas().style.cursor = 'pointer'
      })
      map.current.on('mouseleave', 'municipality-clusters', () => {
        if (map.current) map.current.getCanvas().style.cursor = ''
      })
      map.current.on('mouseenter', 'municipality-points', () => {
        if (map.current) map.current.getCanvas().style.cursor = 'pointer'
      })
      map.current.on('mouseleave', 'municipality-points', () => {
        if (map.current) map.current.getCanvas().style.cursor = ''
      })

      map.current.on('mousemove', 'state-fills', (e) => {
        if (e.features && e.features[0] && map.current) {
          map.current.setFilter('state-fills-hover', [
            '==',
            'name',
            e.features[0].properties?.name || '',
          ])
        }
      })

      setMapLoaded(true)
      setMapZoom(map.current.getZoom())
    })

    map.current.on('zoomend', () => {
      if (!map.current) return
      setMapZoom(map.current.getZoom())
    })

    return () => {
      if (map.current) {
        map.current.remove()
        map.current = null
      }
    }
  }, [onLocationSelect])

  // Update map when data or filters change
  useEffect(() => {
    updateMapData()
  }, [updateMapData])

  // Handle search
  useEffect(() => {
    if (!searchQuery || !map.current || !MAPBOX_TOKEN) return

    const searchLocation = async () => {
      try {
        const response = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
            searchQuery
          )}.json?country=mx&access_token=${MAPBOX_TOKEN}`
        )
        const data = await response.json()

        if (data.features && data.features[0]) {
          const [lng, lat] = data.features[0].center
          map.current?.flyTo({ center: [lng, lat], zoom: 10, duration: 2000 })
        }
      } catch (error) {
        console.error('Geocoding error:', error)
      }
    }

    searchLocation()
  }, [searchQuery])

  const enabledCrimeTypeCount = Object.values(filters.crimeTypes).filter(Boolean).length

  const activeColorScale = mapZoom >= 6 ? municipalColorScale : stateColorScale

  return (
    <div ref={mapContainer} className="w-full h-full">
      {mapLoaded && !tokenMissing && enabledCrimeTypeCount > 0 && activeColorScale && (
        <div className="absolute bottom-4 right-4 z-10 bg-[var(--color-bg-card)]/90 backdrop-blur border border-[var(--color-border)] rounded-lg p-3">
          <div className="text-xs font-medium text-[var(--color-text)] mb-2">
            {mapZoom >= 6
              ? 'Hotspots (municipios) · delitos seleccionados (total)'
              : 'Tasa /100k (estados) · delitos seleccionados'}
          </div>
          <div className="flex items-center gap-1">
            {activeColorScale.colors.map((color, idx) => (
              <div
                key={`${color}-${idx}`}
                className="w-5 h-2 rounded-sm"
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
          <div className="mt-2 flex justify-between text-[var(--color-text-muted)] text-[11px] tabular-nums">
            <span>{activeColorScale.min.toFixed(0)}</span>
            <span>{activeColorScale.max.toFixed(0)}</span>
          </div>
        </div>
      )}
      {tokenMissing && (
        <div className="absolute inset-0 flex items-center justify-center bg-bg-dark">
          <div className="text-center max-w-md p-6 bg-bg-card rounded-lg border border-border-main">
            <div className="text-4xl mb-4">🗺️</div>
            <h2 className="text-xl font-semibold mb-2 text-text-main">Mapbox Token Required</h2>
            <p className="text-text-muted mb-4">Create a .env file with your Mapbox token.</p>
            <pre className="bg-bg-dark p-3 rounded text-left text-sm">
              <code>VITE_MAPBOX_TOKEN=pk.your_token</code>
            </pre>
          </div>
        </div>
      )}
      {!mapLoaded && !tokenMissing && (
        <div className="absolute inset-0 flex items-center justify-center bg-bg-dark">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-text-muted">Cargando mapa...</p>
          </div>
        </div>
      )}
    </div>
  )
}

function getRiskLevel(rate: number): 'low' | 'medium' | 'high' | 'very_high' {
  if (rate < 400) return 'low'
  if (rate < 550) return 'medium'
  if (rate < 700) return 'high'
  return 'very_high'
}
